import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AdminPage from "./AdminPage";
import IntakeForm from "./IntakeForm";
import PrivacyPolicy from "./PrivacyPolicy";
import CookieBanner from "./CookieBanner";
import MostReadBlog from "./MostReadBlog";
import { 
  GraduationCap, 
  Search, 
  FileText, 
  Briefcase, 
  Users, 
  Accessibility, 
  ChevronLeft, 
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Download,
  Calendar,
  Menu,
  X,
  Quote,
  ExternalLink,
  BookOpen,
  ShieldCheck
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/logo.png";

// Time constants for date formatting
const MS_PER_HOUR = 1000 * 60 * 60;       // 3,600,000
const MS_PER_DAY = MS_PER_HOUR * 24;       // 86,400,000

// Navigation Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-amber-100 shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img src={LOGO_URL} alt="Pathways to Prosperity" className="h-20 w-auto" />
          </div>
          
          <div className="hidden lg:flex items-center gap-8">
            <button onClick={() => scrollToSection('about')} className="nav-link" data-testid="nav-about">About</button>
            <button onClick={() => scrollToSection('services')} className="nav-link" data-testid="nav-services">Services</button>
            <button onClick={() => scrollToSection('stories')} className="nav-link" data-testid="nav-stories">Success Stories</button>
            <button onClick={() => scrollToSection('resources')} className="nav-link" data-testid="nav-resources">Resources</button>
            <button onClick={() => scrollToSection('blog')} className="nav-link" data-testid="nav-blog">Blog</button>
            <a href="tel:+18447286843" className="text-emerald-700 font-semibold hover:text-emerald-800 transition-colors text-sm" data-testid="nav-phone">
              1-844-7-AUNTIE
            </a>
            <a href="mailto:sheila@mypath2prosperity.com" className="text-emerald-700 font-bold italic hover:text-emerald-800 transition-colors text-sm" data-testid="nav-email">
              Sheila@MyPath2Prosperity.com
            </a>
            <button onClick={() => scrollToSection('contact')} className="btn-primary" data-testid="nav-consultation">
              Schedule Consultation
            </button>
          </div>

          <button 
            className="lg:hidden p-2 text-gray-700"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {isOpen && (
          <div className="lg:hidden py-4 border-t border-amber-100 animate-fadeIn">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('about')} className="nav-link-mobile">About</button>
              <button onClick={() => scrollToSection('services')} className="nav-link-mobile">Services</button>
              <button onClick={() => scrollToSection('stories')} className="nav-link-mobile">Success Stories</button>
              <button onClick={() => scrollToSection('resources')} className="nav-link-mobile">Resources</button>
              <button onClick={() => scrollToSection('blog')} className="nav-link-mobile">Blog</button>
              <a href="tel:+18447286843" className="text-emerald-700 font-semibold text-center py-2">1-844-7-AUNTIE (1-844-728-6843)</a>
              <a href="mailto:sheila@mypath2prosperity.com" className="text-emerald-700 font-bold italic text-center py-1"><strong><em>Sheila@MyPath2Prosperity.com</em></strong></a>
              <button onClick={() => scrollToSection('contact')} className="btn-primary w-full">
                Schedule Consultation
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Colleges List Data
const collegesList = [
  { name: "Harvard College", url: "https://www.harvard.edu" },
  { name: "Harvard Law School", url: "https://hls.harvard.edu" },
  { name: "Columbia University", url: "https://www.columbia.edu" },
  { name: "Cornell University", url: "https://www.cornell.edu" },
  { name: "University of Pennsylvania", url: "https://www.upenn.edu" },
  { name: "Penn Carey Law School", url: "https://www.law.upenn.edu" },
  { name: "Bowdoin College", url: "https://www.bowdoin.edu" },
  { name: "Carleton College", url: "https://www.carleton.edu" },
  { name: "Hamilton College", url: "https://www.hamilton.edu" },
  { name: "Mount Holyoke", url: "https://www.mtholyoke.edu" },
  { name: "Brandeis University", url: "https://www.brandeis.edu" },
  { name: "Case Western Reserve", url: "https://www.case.edu" },
  { name: "Kenyon College", url: "https://www.kenyon.edu" },
  { name: "Oberlin College", url: "https://www.oberlin.edu" },
  { name: "Clemson University", url: "https://www.clemson.edu" },
  { name: "Washington University-St. Louis", url: "https://www.wustl.edu" },
  { name: "Davidson College", url: "https://www.davidson.edu" },
  { name: "Sarah Lawrence College", url: "https://www.sarahlawrence.edu" },
  { name: "University of Maryland, College Park", url: "https://www.umd.edu" },
  { name: "Southern Methodist University", url: "https://www.smu.edu" },
  { name: "West Virginia University", url: "https://www.wvu.edu" },
  { name: "Duquesne University", url: "https://www.duq.edu" },
  { name: "The University of Virginia", url: "https://www.virginia.edu" },
  { name: "College of William and Mary", url: "https://www.wm.edu" },
  { name: "University of St. Andrews (Scotland)", url: "https://www.st-andrews.ac.uk" },
  { name: "University of Michigan", url: "https://umich.edu" },
  { name: "The Pennsylvania State University", url: "https://www.psu.edu" },
  { name: "University of Texas-Austin", url: "https://www.utexas.edu" },
  { name: "Rensselaer Polytechnic Institute", url: "https://www.rpi.edu" },
  { name: "Denison University", url: "https://denison.edu" },
  { name: "Hood College", url: "https://www.hood.edu" },
  { name: "Mount Saint Mary's University (MD)", url: "https://msmary.edu" },
  { name: "Georgetown University", url: "https://www.georgetown.edu" },
  { name: "Boston University", url: "https://www.bu.edu" },
  { name: "New England Law", url: "https://www.nesl.edu" },
  { name: "University of Georgia", url: "https://www.uga.edu" },
  { name: "Arizona State University", url: "https://www.asu.edu" },
  { name: "University of South Carolina", url: "https://www.sc.edu" },
  { name: "The Ohio State University", url: "https://www.osu.edu" },
  { name: "University of North Carolina-Chapel Hill", url: "https://www.unc.edu" },
  { name: "Virginia Tech", url: "https://www.vt.edu" },
  { name: "Rutgers University", url: "https://www.rutgers.edu" }
];

// Hero Section
const HeroSection = () => {
  const stats = [
    { value: "$50M+", label: "Scholarships Secured (USD)" },
    { value: "100%", label: "Placement Rate" },
    { value: "100%", label: "Commitment" }
  ];

  return (
    <section id="hero" className="relative pt-24 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-emerald-50/30"></div>
      <div className="absolute top-20 right-0 w-96 h-96 bg-emerald-100/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-100/50 rounded-full blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-8 animate-slideUp">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              <span className="text-emerald-700 font-medium text-sm">Empowering Students Since 2015</span>
            </div>
            
            <div className="flex justify-center lg:justify-start mb-2">
              <img src={LOGO_URL} alt="Pathways to Prosperity Logo" className="h-56 w-56 object-contain" />
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              Your Pathway to <span className="text-emerald-700">Prosperity</span>
            </h1>
            
            <h2 className="text-xl sm:text-2xl text-gray-600 font-medium">
              Educational Opportunity for the REST of US
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
              We proudly provide guidance for High School, College/University, Workforce Training for ALL STUDENTS, without regard to family financial status.
            </p>

            <ul className="space-y-3 text-gray-600">
              {[
                "First Generation Undergraduate/Graduate College Students",
                "Students of ANY financial/socioeconomic status",
                "FBA and BIPOC Students (Biracial, Indigenous, and People of Color)",
                "Students with IEPs, 504s, and Learning/Emotional/Physical Disabilities",
                "American students interested in International University Admissions (Undergraduate/Graduate/Professional)",
                "Students interested in Workforce Training and High-Demand Career Education",
                "Adult Learners and Returning Students"
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-4 pt-4">
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary text-lg px-8 py-4"
                data-testid="hero-cta-journey"
              >
                Start Your Journey
              </button>
              <button 
                onClick={() => document.getElementById('stories')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary text-lg px-8 py-4"
                data-testid="hero-cta-stories"
              >
                Read Success Stories
              </button>
            </div>

            <div className="pt-4 flex flex-wrap items-center gap-4">
              <a href="tel:+18447286843" className="inline-flex items-center gap-2 text-emerald-700 font-bold text-xl hover:text-emerald-800 transition-colors" data-testid="hero-phone">
                <Phone size={22} />
                1-844-7-AUNTIE (1-844-728-6843)
              </a>
              <a href="mailto:sheila@mypath2prosperity.com" className="inline-flex items-center gap-2 text-emerald-700 font-bold italic text-xl hover:text-emerald-800 transition-colors" data-testid="hero-email">
                <Mail size={22} />
                <strong><em>Sheila@MyPath2Prosperity.com</em></strong>
              </a>
            </div>
          </div>

          <div className="relative animate-slideUp animation-delay-200">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1741699427799-3fbb70fce948?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwY29sbGVnZSUyMHN0dWRlbnRzJTIwc3R1ZHlpbmclMjBsaWJyYXJ5fGVufDB8fHx8MTc3MTIyNTU2NXww&ixlib=rb-4.1.0&q=85"
                alt="Diverse students studying together"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
            
            {/* Floating Stats */}
            <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-xl border border-amber-100 animate-float">
              <div className="text-4xl font-bold text-emerald-700">$50M+</div>
              <div className="text-gray-600">Scholarships Secured</div>
            </div>
            
            <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-6 shadow-xl border border-amber-100 animate-float animation-delay-300">
              <div className="text-4xl font-bold text-amber-600">100%</div>
              <div className="text-gray-600">Placement Rate</div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, i) => (
            <div 
              key={stat.label} 
              className="bg-white rounded-2xl p-6 shadow-lg border border-amber-100 text-center hover:shadow-xl transition-shadow"
              data-testid={`stat-${i}`}
            >
              <div className="text-4xl font-bold text-emerald-700">{stat.value}</div>
              <div className="text-gray-600 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Auntie Education YouTube Section */}
        <div className="mt-16 bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-amber-100" data-testid="auntie-education-section">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* TV Frame */}
            <div className="relative">
              <div className="bg-gray-900 rounded-2xl p-4 shadow-2xl">
                <div className="bg-gray-800 rounded-xl overflow-hidden aspect-video relative">
                  <a 
                    href="https://www.youtube.com/@AuntieEducation" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full h-full"
                    data-testid="auntie-education-link"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_recovery-helper-6/artifacts/oobsprt0_IMG_0570.png"
                      alt="Auntie Education - Sheila Coleman-Castells"
                      className="w-full h-full object-contain bg-gradient-to-br from-amber-100 to-blue-100 hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/10 hover:bg-black/0 transition-colors flex items-center justify-center">
                      <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-lg hover:bg-red-700 transition-colors">
                        <div className="w-0 h-0 border-t-[12px] border-t-transparent border-l-[20px] border-l-white border-b-[12px] border-b-transparent ml-1"></div>
                      </div>
                    </div>
                  </a>
                </div>
                {/* TV Stand */}
                <div className="flex justify-center mt-4">
                  <div className="w-24 h-2 bg-gray-700 rounded-full"></div>
                </div>
              </div>
              {/* TV Base */}
              <div className="flex justify-center">
                <div className="w-32 h-4 bg-gray-800 rounded-b-lg -mt-1"></div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-4">
              <h3 className="font-display text-3xl font-bold text-gray-900">
                Check out our coming YouTube Channel, <span className="text-red-600">"Auntie Education"</span>!
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Auntie Education (the alter ego of Sheila Coleman-Castells, M.Ed.) is your source for ALL THINGS Educational, from K-12, Undergraduate, Graduate and Professional Schools (ALL Majors), as well as Workforce Education: Training for Pre- and Full Apprenticeships and Courses for all Skills-based professions such as Plumbing, Carpentry/Framing, Welding, Solar/Wind/Installation, Electrician, HVAC Technician, Allied Health Professions, and so much more.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Whether you are a student, a parent, a grandparent, or an Educator looking to learn more, Auntie knows it all, and is ready to spill all the tea—and will eat all the cookies and leave no crumbs!
              </p>
              <p className="text-emerald-700 font-semibold text-lg">
                Look for your favorite Auntie in Mid-June, 2026
              </p>
              <a 
                href="https://www.youtube.com/@AuntieEducation"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="youtube-subscribe-btn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                Visit Auntie Education on YouTube
              </a>
            </div>
          </div>
        </div>

        {/* Colleges List Section */}
        <div className="mt-16 bg-gradient-to-br from-emerald-50 to-amber-50 rounded-3xl p-8 lg:p-12 border border-amber-100" data-testid="colleges-section">
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
            To What Colleges/Universities Have Our Students Been Accepted and/or Graduated?
          </h3>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
            {collegesList.map((college, i) => (
              <a 
                key={college.name}
                href={college.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-emerald-700 hover:text-emerald-800 hover:underline transition-colors py-1"
                data-testid={`college-${i}`}
              >
                {college.name}
              </a>
            ))}
          </div>
          <p className="mt-6 text-gray-600 text-center italic">
            In addition, we have placed students in various high school summer programs all over the country in a myriad of college majors, from the Arts to Engineering, and so much more.
          </p>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const universities = [
    "University of Virginia",
    "University of Maryland, College Park",
    "University of North Carolina at Chapel Hill",
    "George Washington University",
    "American University",
    "Howard University",
    "West Virginia University",
    "United Arab Emirates University"
  ];

  return (
    <section id="about" className="py-24 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-slideUp">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="Pathways to Prosperity Logo" className="h-24 w-24 object-contain" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            About Pathways to Prosperity
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Dedicated to breaking down barriers and opening doors to higher education for underrepresented communities.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div className="space-y-8 animate-slideUp">
            <h3 className="font-display text-3xl font-bold text-gray-900">
              Every Student Deserves a Path Forward
            </h3>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              At Pathways to Prosperity, LLC, we believe that financial circumstances, race, or disability should never be barriers to educational opportunity. Founded to serve ALL students of any race or ethnic background, socio-economic level, or ability, we provide personalized college or workforce development skills that transform dreams into realities.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              Our comprehensive approach connects students and families with the right educational opportunities—whether that's a four-year university, community college, or workforce training program—while securing the scholarships and financial aid needed to make it possible.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 pt-4">
              {[
                { title: "Our Mission", desc: "To empower underrepresented students to achieve their educational and career goals." },
                { title: "Our Values", desc: "Equity, accessibility, and unwavering belief in every student's potential." },
                { title: "Who We Serve", desc: "Working- and Middle-class families, BIPOC students, and individuals with disabilities." },
                { title: "Our Approach", desc: "Personalized guidance, scholarship expertise, and family-centered support." }
              ].map((item, i) => (
                <div key={item.title} className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="animate-slideUp animation-delay-200">
            <div className="bg-gradient-to-br from-emerald-50 to-amber-50 rounded-3xl p-8 border border-amber-100">
              <div className="text-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_recovery-helper-6/artifacts/t55wkd79_IMG_0569.png"
                  alt="Sheila Coleman-Castells, M.Ed."
                  className="w-64 h-64 mx-auto mb-6 object-cover object-top rounded-2xl shadow-lg"
                />
                <img 
                  src={LOGO_URL}
                  alt="Pathways to Prosperity Logo"
                  className="w-24 h-24 mx-auto mb-4 object-contain"
                />
                <h4 className="font-display text-2xl font-bold text-gray-900 mb-2">
                  Sheila Coleman-Castells, M.Ed.
                </h4>
                <p className="text-emerald-700 font-medium mb-4">Founder & Lead Counselor</p>
                <a 
                  href="mailto:sheila@mypath2prosperity.com"
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-emerald-700 transition-colors font-bold italic"
                  data-testid="founder-email"
                >
                  <Mail size={18} />
                  <strong><em>Sheila@MyPath2Prosperity.com</em></strong>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Educational Background and Experience */}
        <div className="mt-20" data-testid="background-section">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 mb-8 underline decoration-emerald-700 decoration-2 underline-offset-8">
              Educational Background and Experience
            </h3>

            <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
              <p>
                Pathways to Prosperity is led by a nationally recognized Educator—Professor, Strategist and policy expert whose career spans more than three decades across K–12, higher education, federal government, and international institutions.
              </p>

              <p>
                She holds a <strong>B.A. in French Language & Literature</strong> (Spanish minor) from George Mason University, a <strong>Master's in French Literature and Pedagogy</strong> from Universit&eacute; de Neuch&acirc;tel, and an <strong>M.Ed. in Secondary Education</strong> with Bilingual/Multicultural Education and both Special Education/TESL certification from George Mason University. Additionally, Sheila initiated doctoral coursework (ABD) in Educational Research at the University of Virginia before both of her parents died mid-coursework.
              </p>

              <p>
                Her career began with <strong>10 years as a K–12 Special Education/Bilingual-ESL teacher</strong> in one of the most competitive education markets in the United States (District of Columbia and Fairfax County (VA) Pub. Schools), where she developed deep expertise in student development, curriculum and instructional differentiation, and family engagement.
              </p>

              <p>
                She went on to serve for <strong>27 years as a college professor</strong>, teaching at leading institutions, including:
              </p>

              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 pl-4 py-4">
                {universities.map((uni) => (
                  <div key={uni} className="flex items-center gap-3 py-1">
                    <span className="w-2 h-2 bg-emerald-600 rounded-full flex-shrink-0"></span>
                    <span className="font-semibold text-emerald-800">{uni}</span>
                  </div>
                ))}
              </div>

              <p>
                In federal service, she served as a <strong>Program Officer at the U.S. Departments of Education and U.S. Department of Labor</strong> under two presidential administrations, evaluating, funding, and advising major national education and workforce initiatives and conducting research.
              </p>

              <p>
                She also served as <strong>Director of Testing for the National Catholic Educational Association</strong> in Washington, DC.
              </p>

              <p>
                A highly accomplished <strong>Education lobbyist, grant strategist, nonprofit leader and researcher</strong>, she has helped secure over <strong>$75 million in funding</strong> for nonprofits, educational institutions, and workforce initiatives across the United States and has been profiled in publications and media around the world.
              </p>
            </div>

            {/* A Life of Service and Commitment */}
            <div className="mt-12 bg-gradient-to-br from-amber-50 to-emerald-50/50 rounded-2xl p-8 border border-amber-100">
              <h4 className="font-display text-xl font-bold text-gray-900 mb-6">
                A Life of Service and Commitment
              </h4>
              <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
                <p>
                  Beyond her professional accomplishments, she is deeply committed to community, faith, and family.
                </p>
                <p>
                  For more than a decade, she has personally <strong>mentored and placed over 30 Appalachian and Mid-Atlantic students</strong> into elite universities with full or near-full scholarships—at no cost to their families.
                </p>
                <p>
                  She is a former Boy Scout leader and <strong>Eagle Scout Mom (Class of 2016)</strong> and a devoted parishioner of:
                </p>
                <ul className="pl-6 space-y-1">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0"></span>
                    St. Augustine Catholic Church (Washington, DC)
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0"></span>
                    St. Katharine Drexel Catholic Church (Boston, MA)
                  </li>
                </ul>
                <p>
                  …and a proud parent of a <strong>Bowdoin College Alumnus, (Class of 2021)</strong>.
                </p>
                <p>
                  Sheila is also a parent to two "fuzzy babies", <strong>Kipper and Tache</strong>.
                </p>
                <div className="mt-6 text-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_779f0f3c-f357-414a-a8a9-c75e32a49446/artifacts/1nh7g7t9_IMG_2292.jpeg"
                    alt="Kipper and Tache - Sheila's beloved dogs"
                    className="max-w-sm w-full mx-auto rounded-2xl shadow-lg border-4 border-white"
                    data-testid="dogs-photo"
                  />
                  <p className="text-gray-500 mt-3 italic text-base">Kipper & Tache</p>
                </div>
              </div>
            </div>

            {/* My Philosophy */}
            <div className="mt-16" data-testid="philosophy-section">
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 mb-8 underline decoration-emerald-700 decoration-2 underline-offset-8">
                My Philosophy
              </h3>

              <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
                <blockquote className="border-l-4 border-emerald-700 pl-6 py-2 bg-emerald-50/50 rounded-r-xl text-xl italic text-gray-800">
                  <p>"I do not simply advise students. I guide families.</p>
                  <p>I do not follow trends. I design pathways.</p>
                  <p>And I tell families what they need to hear—not just what they want to hear."</p>
                </blockquote>

                <p>
                  I do this work because I believe deeply that a young person's education is one of the last, best, and most enduring gifts parents can give their children.
                </p>

                <p>
                  At Pathways to Prosperity, we do not look at students as transcripts or test scores. We look at the <strong>whole child</strong>—their interests, their gifts, their personality, their hopes—and we place those qualities within the context of real-world systems: education, workforce pathways, certifications, costs, and long-term outcomes.
                </p>

                <p>
                  Together, we build a strategy that accounts for all of it. Not in fragments, but as a whole—what I often call a <em>seamless garment</em>—something your child can wear and grow into over time, as they move from student to independent professional.
                </p>

                <p>
                  I am candid with families. I speak the truth as we understand it—clearly, directly, and with care. At the same time, I leave room for change, for growth, and for the unexpected turns that life sometimes brings.
                </p>

                <p>
                  From there, we build an achievable plan—one that, if followed, leads to:
                </p>

                <ul className="pl-6 space-y-2">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></span>
                    <span>strong preparation for college</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></span>
                    <span>a meaningful and affordable undergraduate experience</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></span>
                    <span>and a graduate or professional pathway aligned with the life your child truly wants to lead</span>
                  </li>
                </ul>

                <p>
                  I do not disappear after applications are submitted. I remain alongside my students and their families for years, watching them grow, adjust, succeed, and evolve.
                </p>

                <p>
                  I am as proud of them on the first day we meet as I am when they graduate from Medical School, Law School, Dentistry, or earn their Ph.D.
                </p>

                <p className="font-semibold text-emerald-800 text-xl">
                  They will always be my students. I will always be their Auntie.
                </p>

                <p>
                  And you, my fellow parents, will always be my cherished partners in this journey.
                </p>

                <p className="text-center font-display text-xl font-bold text-gray-900 pt-4 pb-2 italic">
                  Because it truly does take a village to raise a child.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: GraduationCap,
      title: "College Discovery",
      desc: "We help students explore colleges and universities that align with their academic interests, career goals, and personal needs.",
      featured: true
    },
    {
      icon: Search,
      title: "Scholarship Search",
      desc: "Our team identifies scholarship opportunities specifically for Working- and Middle-class, BIPOC, and students with disabilities.",
      featured: true
    },
    {
      icon: FileText,
      title: "Application Support",
      desc: "From essays to financial aid forms, we guide families through every step of the application process."
    },
    {
      icon: Briefcase,
      title: "Workforce Training",
      desc: "For students interested in vocational paths, we connect them with quality training programs and apprenticeships."
    },
    {
      icon: Users,
      title: "Family Advocacy",
      desc: "We work with families to navigate complex educational systems and advocate for their students' needs."
    },
    {
      icon: Accessibility,
      title: "Disability Services",
      desc: "Specialized support for students with disabilities to find schools with robust accommodation programs."
    },
    {
      icon: Phone,
      title: "Chat with Sheila",
      desc: "FREE_CONSULT_CARD",
      featured: true
    }
  ];

  return (
    <section id="services" className="py-24 bg-amber-50/50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-slideUp">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="Pathways to Prosperity Logo" className="h-24 w-24 object-contain" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive support for every step of your educational journey.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => (
            <div 
              key={service.title}
              className={`bg-white rounded-2xl p-8 border border-amber-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${
                service.featured ? 'lg:row-span-1 ring-2 ring-emerald-500/20' : ''
              }`}
              data-testid={`service-${i}`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                service.featured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}>
                <service.icon size={28} />
              </div>
              <h3 className="font-display text-xl font-bold text-gray-900 mb-3">
                {service.title}
              </h3>
              {service.desc === "FREE_CONSULT_CARD" ? (
                <>
                <p className="text-gray-600 leading-relaxed">
                  Pathways to Prosperity offers all parents a <strong>FREE, one-time 40-minute call or Google Meet conference with Sheila</strong> to discuss you and/or your child's specific situation(s), to see how she can help you and your family. Use this link{' '}
                  <a href="https://calendar.app.google/pPvPzrqKNFoQEjdx5" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold underline hover:text-emerald-800">HERE</a>
                  {' '}to make an appointment and be sure to complete the intake form{' '}
                  <a href="/intake" className="text-emerald-700 font-semibold underline hover:text-emerald-800">HERE</a>
                  {' '}and give her some information on you/your child (age, prospective major, current coursework, extracurricular, specific needs, and your particular questions, etc.) so that your time can be used wisely. Sheila looks forward to meeting you!
                </p>
                <p className="mt-3 flex flex-wrap items-center gap-4">
                  <a href="tel:+18447286843" className="inline-flex items-center gap-2 text-emerald-700 font-bold hover:text-emerald-800 transition-colors">
                    <Phone size={18} />
                    1-844-7-AUNTIE (1-844-728-6843)
                  </a>
                  <a href="mailto:sheila@mypath2prosperity.com" className="inline-flex items-center gap-2 text-emerald-700 font-bold italic hover:text-emerald-800 transition-colors">
                    <Mail size={18} />
                    <strong><em>Sheila@MyPath2Prosperity.com</em></strong>
                  </a>
                </p>
                </>
              ) : (
                <p className="text-gray-600 leading-relaxed">
                  {service.desc}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Ready to explore how we can help you?</p>
          <button 
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary"
            data-testid="services-cta"
          >
            Schedule a Free Consultation
          </button>
        </div>
      </div>
    </section>
  );
};

// Success Stories Section
const SuccessStoriesSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const stories = [
    {
      name: "J. Miller",
      school: "William & Mary + University of St Andrews, Scotland",
      year: "Class of 2025",
      achievement: "Perfect LSAT (180) | White House Economist | Harvard Law Class of 2029",
      quote: "Sheila saw my future before I did, before even my parents did, and she set me on that course knowing that I would succeed. If you ask Sheila to work with you, know that she will see who you really are inside—often long before you do.",
      fullStory: `It is a supreme pleasure to write this letter to give my highest possible recommendation of my dear mentor and friend, Sheila Coleman-Castells. I met Sheila as a young child in Boy Scouts. She was a Scout Leader and Committee Member for many years who guided me and her son, John-Paul, toward obtaining our Eagle Scout designations, with six Eagle Palms.

As a rising senior in high school during the COVID-19 era in 2020, she asked me about my plans for college. I had a 4.6 GPA in high school, was going to graduate with a concurrent Associate's Degree as Valedictorian of the class, and had many accolades, but my family's knowledge of the academic world was very limited. My Mother was the first and only college graduate in both families, and because she went to a state school, our horizons were limited by her accomplishments, thinking that anything else was simply out of reach fantasies.

When I told Sheila of my ambivalence and chagrin regarding what I felt were my choices, she surprised me with the fact that I was eligible, due to my academic background alone, to attend some of the best schools in the country and that she could get me there. This news was an incredibly motivating boost during a scary and difficult time facing senior year in the COVID era.

Among many applications that year, we applied for a joint Bachelor's program with William & Mary and the University of St Andrews in Scotland (two years at each, one degree from each school). It was one of the best days of my life to be accepted in this program, one of only 30 students selected each year. I never thought, when I first started this journey, that I could study in the United Kingdom, but I completed both programs in 2025 with a degree majoring in Economics and minoring in Classics from William & Mary, and a B.A. (International Honours) from the School of Economics and Finance at St Andrews.

As I write this in 2025, I am an Economist at the White House, something that I never dreamed in 2020 that I might be. I have taken the LSAT and obtained a perfect score (180). As of March 2026, I have been accepted into Harvard Law School, Class of 2029!

Working with Sheila over these years has given me, a young man from a very small town in the Appalachian mountains of Maryland, a perspective that I could never have had if we hadn't met all those years ago in Boy Scouts. Choose Sheila because you want the best for your child or if you are a young person, you want the best for yourself. Her job is not ONLY to help you find a college and get you in, but to help you find yourself in that college journey and choose the place where you truly BELONG for four years.`
    },
    {
      name: "Quintin Utterback",
      school: "Hamilton College",
      year: "First-Generation College Student",
      achievement: "Accepted to Dream School + Full Financial Aid Package",
      quote: "I wanted to formally thank you for all of the time and effort you put into guiding my family and I through the college application process, as I am certain my prospects for college education would have looked very different had you not been there to show us the path.",
      fullStory: `Now that I've successfully completed my first year of college at Hamilton, I've been thinking back on the things that helped me get to this point. I wanted to formally thank you for all of the time and effort you put into guiding my family and I through the college application process, as I am certain my prospects for college education would have looked very different had you not been there to show us the path.

I recall the list of colleges you suggested I look into, and how I had never heard of any of those institutions previously. Coming from Southern Garrett High School, I never received significant guidance regarding college prospects through school, so having your support when exploring my options was extremely helpful.

I also wanted to thank you for aiding me when I was writing my college application essay and preparing for interviews. The single largest source of my anxiety throughout my college search was not knowing what skills and experiences colleges were looking for in potential students. This lack of information on how to prepare led me to consider sending less information to colleges I was interested in, rather than risking sending something that would make me look bad. However, you were there to steer me in the right direction and help me prepare. You helped me realize that I had all the skills and abilities that colleges were looking for, and you even helped me learn how to market myself to institutions that I was really interested in attending.

Lastly, although I was less personally involved in it, I wanted to thank you for advising my parents through the financial aid process after I got accepted at Hamilton. Although I assumed the most stressful part of applying to college had finished, I was deeply mistaken. For a few months there was a very real concern that I would not be able to attend my dream college because of financial restraints. However, you again came to the rescue by suggesting a variety of scholarships to pursue and showing my parents how to appeal for greater financial aid.

All together, I wanted to thank you for helping me reach what I hope is my full potential. My first year at Hamilton has gone by with strong grades and good experiences to look back on. The cherry on top was the care-package you sent me during finals week, which made the most stressful week of my life a bit more bearable. I am forever grateful for the support you have given me, and I hope you will continue to guide ambitious students towards bigger and better futures.`
    },
    {
      name: "Andrea Tyree-Castells, M.P.P.",
      school: "Brandeis University - Heller School",
      year: "Class of 2022",
      achievement: "Full Tuition Scholarship + Accepted to Harvard & Michigan | Now at NYU School of Law",
      quote: "Sheila Coleman-Castells is an exceptional mentor with a comprehensive skill set that is invaluable for anyone preparing for higher education. Her expertise, dedication, and genuine interest in her mentees' success uplifts all those around her.",
      fullStory: `I am writing to enthusiastically recommend Sheila Coleman-Castells as a mentor for individuals preparing for higher education. Sheila played an instrumental role in my graduate school application process, guiding me toward a Master of Public Policy from Brandeis University's Heller School for Social Policy and Management. Sheila has demonstrated exceptional qualities that make her an outstanding candidate for ushering others into and through higher education.

When I met Sheila, I was working at a grassroots organizing non-profit, Our Future West Virginia. As a Black Appalachian, I was aware of the racial and economic disparities that plagued my community and was eager to play a part in Our Future's efforts to spark bottom-up change around education, housing, and criminal justice. I had recently returned from a two-year deployment to Timor-Leste as a Peace Corps' Community Economic Development Volunteer. In living abroad, I learned that, often, the people who create the most necessary and sustainable change in a community are the members of that community. I was ready to make change in my community.

Yet while at Our Future, I observed the limitations of grassroots efforts. When a young, unarmed, Black woman was beaten by a municipal police officer in the capital city of Charleston, my colleagues and I lobbied our local officials to examine and reform the police department's use of force policy. Despite significant pressure from the community, the mayor and police chief only established a task force to discuss the issue. No real policy or practice changes emerged. No one had answers on how to advance institutional change in policing. I needed to learn how we could shift the state of public safety, and while I knew this level of expertise would require a higher degree, I had no clue what that degree could be. Enter Sheila.

I was connected to Sheila through my colleague. He described her as "the woman who will get you into graduate school." In our first conversation, Sheila imparted the wisdom and guidance that only a lifelong educator could possess. She had an extensive awareness of various graduate programs and career opportunities, helping me identify the ones that would put me on the path to shifting how this country addresses public safety. Sheila used the Socratic method to guide me through drawing the distinctions between policy and law. She opened my eyes to the possibilities of a public policy graduate program, vowing that I could be accepted into any program, even Harvard University's John F. Kennedy School of Government. While I didn't possess that level of confidence in myself, Sheila never wavered.

Over the course of five months, Sheila guided me through every step of the application process. She directed me in the best approach to prepare for the GRE exam. Following her study method, I excelled on my first attempt at the exam. She assisted me in identifying the top programs where I could focus my studies on racial disparities and criminal justice: Harvard's Kennedy School, Brandeis' Heller School, and the University of Michigan's Rackham Graduate School.

Sheila possesses a profound ability to critically analyze social issues and explore various approaches to addressing them. This skill was particularly valuable as it helped me frame my application within the broader context of societal impact, allowing me to present my aspirations and intentions with clarity and conviction. Moreover, Sheila has an exceptional talent for synthesizing varying career goals into a coherent and compelling narrative. Her guidance enabled me to articulate my diverse interests and experiences in a way that was both logical and persuasive, ensuring that my application told a clear and compelling story. Sheila provided thorough and constructive feedback on all of my application materials, raising the quality of my submissions to a level that I would not have achieved on my own. Her keen eye for detail and her ability to kindly articulate constructive criticism were instrumental in refining my application. With Sheila's help, I was accepted to all three public policy programs in which I applied.

Her knowledge of scholarship and application deadlines was invaluable. Sheila meticulously kept track of all relevant dates, ensuring that I met every deadline promptly. This attention to detail significantly reduced the stress associated with the application process and allowed me to focus on crafting a strong application. Additionally, Sheila's access to databases on financial aid and scholarship opportunities was a tremendous benefit. She guided me through the various options available within and outside of my programs, helping me secure the necessary funding to pursue my academic goals. Not only did I receive multiple academic financial aid scholarships from Harvard and Michigan, but I was also awarded a full tuition and fees scholarship from Brandeis.

This full ride was the result of a compelling Diversity and Inclusion Scholarship essay, of which Sheila provided key feedback. One of Sheila's most remarkable abilities is her skill in marketing a student's background, lived experience, and skills. She is adept at shaping a narrative around why the academic program would benefit from the student, emphasizing diversity as a strength. Her approach allowed my application as a Black Appalachian to stand out and underscored my unique contributions to the academic community. I fulfilled those promised contributions during my graduate program, becoming a lauded Eli J. and Phyllis N. Segal Citizen Leadership fellow and being awarded the Heller School's Leadership Award from my professors and classmates.

Sincerely,
Andrea Tyree-Castells
The Policing Project at NYU School of Law
M.P.P. '22 | Brandeis University Heller School for Social Policy and Management
B.A. '17 | Howard University`
    },
    {
      name: "Ares Morris",
      school: "Sarah Lawrence College",
      year: "Transferred from WV Wesleyan",
      achievement: "3.93 GPA | $20K Additional Aid Secured | Studied Abroad at University of Leeds | PhD Track",
      quote: "My experience with Ms. Sheila can only be described as transformative. I am not the same person that I was when I met her. She helped me actualize the potential that lay dormant in me by leading me to the resources that would help facilitate my growth.",
      fullStory: `I am writing today in recommendation of Ms. Sheila Coleman as a mentor. Ms. Sheila and I have been working together since 2019 after I graduated high school and she has been integral in my academic and personal success.

Before we met, I felt aimless. I dreamed of going to college, but I lacked the guidance I needed from my parents and my high school. If it weren't for my friend Addy, I wouldn't have applied to college at all. With his help, I got into my top choice school, but unfortunately, an award letter did not accompany the acceptance letter. I deferred for a year in hopes of working to afford the enrollment and housing deposit, but that proved impossible without the financial support of my parents. Witnessing my struggle, Addy introduced me to his godmother, Ms. Sheila, who offered invaluable guidance.

Ms. Sheila listened to me and provided practical advice that aligned with both my aspiration of being a musician and my financial situation. I accepted her recommendation, and with her connections, I was accepted into West Virginia Wesleyan College on a creative arts scholarship. There, I excelled academically; it was the exact environment I needed to build my confidence as a scholar.

During my time there, I also developed an interest in gender studies, and as my love for the discipline grew, I realized that I needed to be in an institution that could help facilitate my growth in that area. In the fall of 2021, I told her that Wesleyan no longer met my academic needs, and she guided me through the process of transferring colleges.

Ms. Sheila was with me every step of the way, recommending schools based on my academic interests and financial needs and helping me develop strong written materials for my applications. I was accepted into Sarah Lawrence College for the fall of 2022, and with Ms. Sheila's guidance, I successfully negotiated my financial aid package, securing an additional $20k in aid.

At Sarah Lawrence, I continued to thrive, maintaining a 3.93 GPA, earning a competitive internship at the Sylvia Rivera Law Project, growing as a student leader and activist, strengthening my analytical thinking and writing, and becoming an integral part of the student music scene. I completed my final semester at Sarah Lawrence by studying abroad at the University of Leeds, where I studied decolonial theory and began learning French.

Currently, I am taking a gap year to hone my research interests and build my CV before applying for my PhD in the fall. She is more than a mentor to me; I regard her as family. From the bottom of my heart, I am grateful for her wisdom, support, and belief in my success.`
    },
    {
      name: "Mark Clark",
      school: "Father of Makayla Clark, Carleton College",
      year: "Parent Testimonial, January 2025",
      achievement: "Daughter Accepted as QuestBridge Scholar at Carleton College",
      quote: "It is rare to find someone as passionate, knowledgeable, and genuinely invested in the success of others as Sheila. Thanks to her remarkable guidance, my daughter is now thriving in her academic journey and pursuing her dreams with confidence.",
      fullStory: `I am writing this letter with immense gratitude and admiration to recommend my cousin, Ms. Sheila Coleman-Castells, for her outstanding contributions and dedication in guiding my daughter Makayla through her high school journey and college preparation process. Witnessing her expertise firsthand has been nothing short of transformative for our family.

Ms. Coleman-Castells began supporting my daughter when she was a high school sophomore, and from the very beginning, it was evident that she takes no shortcuts. She is tough, but it is through her unwavering love and dedication that she prepared my daughter to soar. Sheila does not sugarcoat reality; instead, she offers honest and constructive feedback that truly equips students for the challenges ahead. Her deep insights as an educator, a parent, and someone who has even crafted test questions for college boards make her guidance uniquely profound and impactful.

What sets Sheila apart is her extraordinary ability to understand what colleges are looking for while maintaining a remarkable knowledge of financial resources at schools from coast to coast. Not only does she identify opportunities like scholarships and grants tailored to each student, but she also examines the broader climate, culture, and fit of each campus. Sheila has the unique skill of tailoring school recommendations to a student's personality, ensuring a lasting and fulfilling match. This holistic approach sets students up for success, both academically and emotionally, as they transition to college life.

Sheila's mentorship extended beyond academics. She provided indispensable help with college preparatory tests, guided my daughter through the art of writing essays that showcased her individuality, and supported us in navigating the often-intimidating world of scholarships and grants, including the prestigious QuestBridge Scholarship Program. Her ability to strategize with precision and compassion has been invaluable.

As a parent, I often felt overwhelmed by the college application process, but Sheila was a constant source of reassurance, wisdom, and encouragement. Her patience and attention to detail—whether it was meeting deadlines, refining essays, or navigating financial aid—were unmatched. She empowered my daughter to dream big, aim high, and approach every challenge with confidence and determination.

It is rare to find someone as passionate, knowledgeable, and genuinely invested in the success of others as Sheila. Thanks to her remarkable guidance, my daughter is now thriving in her academic journey and pursuing her dreams with confidence. I wholeheartedly recommend Ms. Sheila Coleman-Castells to any family, program, or institution in search of an exceptional mentor and advisor. Her ability to inspire and uplift those around her is truly a gift.

Sincerely,
Mark Clark, father of Makayla Clark ('27), student at Carleton College in Minnesota, and QuestBridge Scholar`
    },
    {
      name: "Makayla Clark",
      school: "Carleton College",
      year: "Sophomore, QuestBridge Scholar",
      achievement: "QuestBridge College Prep Scholar | First-Generation from Title I School to Elite Liberal Arts College",
      quote: "From our very first conversation, Sheila affirmed my potential to do great things and push the limits of what I can accomplish. In today's day and age, where all forms of education are being devalued, Sheila's knowledge is a priceless liberatory tool that deserves to be shared with the rest of the world.",
      fullStory: `I owe my current success as a sophomore at the liberal arts school Carleton College to my Cousin Sheila Coleman-Castells. Before meeting Sheila, the future I envisioned for myself was very tiny. Reserved to my small hometown of Katy, Texas and even tinier Title I charter high school, I thought it would be a miracle to get into the big public university systems such as Texas Tech. From our very first conversation, Sheila affirmed my potential to do great things and push the limits of what I can accomplish. And through the business she plans on kickstarting, I am certain she will help other low-income students plan their future in similar ways. In today's day and age, where all forms of education are being devalued, Sheila's knowledge is a priceless liberatory tool that deserves to be shared with the rest of the world.

It was Sheila that first introduced me to both Questbridge's College Prep Scholars and Match Scholarship programs. The application process was new to me and challenged me to write in ways I've never had to before, but Sheila's constant support assured me that I was up for the challenge. Through my participation in both programs (although I was not selected as a finalist for the Match Scholarship) my eyes were open to the wide diversity of school options: such as liberal arts colleges and historically women's colleges. It was through these connections that I was accepted into my current home Carleton College!

Another one of Sheila's strengths is her ability to be flexible and cater her advice to the needs of her students. She is a deeply caring woman that only wants the best for all those around her. Back when I was a highschooler, my main interest was Biology and I was certain I would become a Biostatician one day. As a response, Sheila connected me with schools and research programs of interest. But during my freshman year as an undergrad, I completely changed my mind and chose Religion as my major. Once I broke this news, her support remained unwavering and she continued to affirm my new dreams of becoming a professor one day.

There is no doubt in my mind that other young students seeking academic/career counsel will benefit from Sheila's guidance.

Sincerely,
Makayla Clark
Sophomore at Carleton College
Katy, Texas and Northfield, Minnesota`
    },
    {
      name: "Jill Devrick",
      school: "Mother of Quintin Utterback, Hamilton College",
      year: "Parent Testimonial",
      achievement: "Son Admitted to Hamilton College Class of 2026 | Financial Aid Negotiated Lower Than State School Cost",
      quote: "You took the time to get to know my son and what made him unique. Hamilton, with a published cost of attendance of over $80K per year, became even more affordable than a large state school. We are so thankful to have you as a guide and cheerleader throughout his journey.",
      fullStory: `Dear Sheila,

I am so grateful for the guidance and support you provided to Q and I during his college search. Living in a rural area with limited opportunities for higher education, I wanted to ensure that Q was able to "think outside the box" and consider many different types of schools rather than defaulting to the safest choices. You took the time to get to know my son and what made him unique, and you made excellent suggestions of colleges worth investigating. I am so glad you encouraged us to plan a road trip to visit, tour, and decide which were the most promising schools in terms of Q being a good fit for the school, as well as the school being a good fit for Q. We looked at small colleges, large universities, rural, urban, suburban, close to home, and far away. It was an exciting time of growth for both of us.

As you know, Q is very introverted and has some social anxiety resulting from a mild tic disorder, but he is also a good student and solid communicator in the right settings. You were able to coach and encourage him to stretch himself academically and socially - not to the point of discomfort, but to the point of developing more confidence in his abilities, talents, and convictions. Although he had several viable choices on his list of schools, his first choice quickly became Hamilton College, a very competitive "little ivy" liberal arts school in central New York that focuses on written and oral communication across all majors, with the motto, "Know thyself." You knew that Q's best chance at acceptance was to apply early decision after visiting there twice to show he was a "true believer" in the college's vision for students. Hamilton chose to "defer" deciding on him during the early decision process, but you told us not to be discouraged because he was still in consideration for regular decision. Thank you for walking us through several steps Q took during the early months of 2022 to show his continued interest in Hamilton so that he would stay on their radar as a strong candidate. Your strategy worked, and Q was admitted to the Class of 2026.

Throughout Q's college search one of my biggest concerns was financial. I had some money saved for him in a 529 account, but it wouldn't be enough to cover four years. When I chose where to attend college years ago, my choice was made almost exclusively on finances because I believed that I wouldn't be able to afford any of the top tier, more expensive schools. I am so grateful that you opened my eyes to what was possible regarding scholarships and other financial aid. You encouraged us to consider many factors rather than solely focus on cost. When Q received his financial aid offer from Hamilton, it wasn't enough to make it doable, but you guided me through the communication and negotiation with Hamilton that was necessary to make a case for more aid. And once we received their final offer, you helped us to compare the value of the various schools he was considering before making his final choice. It turned out that Hamilton, with a published cost of attendance of over $80K per year at the time, became even more affordable than a large state school in which he wouldn't have received any scholarships or other aid.

Q has grown by leaps and bounds during his four years at Hamilton College, and we are so thankful to have you as a guide and cheerleader throughout his journey. He will graduate soon with a degree in philosophy and minors in both public policy and creative writing. He will be taking a year or two off from school after graduation to work before continuing his education in counseling services and/or human resources. At one point, he told me, "If I'm not working in a career that helps people, what's the point?" I think he has learned a lot from your example.

Warm regards,
Jill Devrick`
    },
    {
      name: "Kyus Spencer",
      school: "Graduate, Dual Degree: B.S. Aerospace and Mechanical Engineering, Rensselaer Polytechnic Institute, Troy NY",
      year: "Class of 2024",
      achievement: "Dual B.S. in Aerospace & Mechanical Engineering | Accepted Off Waitlist | Tuition Reduced to 1/4 Cost",
      quote: "Without the help of Ms. Sheila during this difficult time in my life, I don't think I would have had the opportunity to get to where I am now. She was beyond an excellent mentor, and gave me an opportunity I don't think I ever would have had without her help.",
      fullStory: `I was fortunate enough to meet Miss Sheila Coleman-Castells from when I was a scout in the Scouts of America. During that time, we had spoken briefly on what my future might look like. I hadn't put much thought into the decision and told her I had no idea. I knew I wanted to work in a scientific field, but I hadn't determined what that might look like or where I could learn. It was a conversation I had not thought much about at the time. A few years after this conversation, I was in my senior year of high school and the college application process had just begun.

My experience applying to colleges was difficult. I had a number of challenges that I faced; COVID, my parents own lack of college application experience, my highschool counselor sending incorrect transcripts to universities I applied at, my highschool counselor sent necessary documents in late, my highschool counselor combined three different people and activities into her recommendation letter, and finally, I started late for the application processes for my chosen universities.

The cards were stacked against me, and if it weren't for Ms. Sheila's guidance, I am not confident that I would have been able to get into a university such as Rensselaer Polytechnic Institute (RPI). Throughout the entirety of the process, she consistently guided me through the application process, what to look for in universities, and how to find universities that were suited to what I was interested in. By this time, I had narrowed down my selections to six schools I wanted to apply to: Rensselaer Polytechnic Institute, Princeton, University of Maryland, University of Notre Dame, University of Purdue, and Embry Riddle. Ms. Sheila kept in consistent contact with me by asking about my application status with the various universities. Due to the number of issues that had plagued my application process, I was fortunate enough to receive two positive responses from the universities I had applied to. University of Maryland was willing to give me spring admission, but was unable to give fall admission due to my late submission. Rensselaer Polytechnic Institute (RPI) had put me on their wait list.

Luckily for me, I was invited to attend RPI. The class of 2020 only had two students that were accepted from the wait list at RPI. Again, Ms. Sheila helped and guided me to ask for more financial aid. This was a large point of discussion within my family for attending higher education. With this guidance, I received a more than sufficient financial aid package where I paid 1/4th the cost of the total tuition and I was able to attend a spectacular university.

Going to RPI was very different from the area I was in, as RPI took COVID much more seriously than in my home town. At RPI, my first month was only online. Even after the first week, everyone was required to do bi-weekly COVID tests. Even Troy itself was a much larger city than anything I had ever experienced before. I struggled quite a bit in my first year of college. It was the mixture of online classes, the new required study regime and time management skills I needed to develop, and recognizing when I needed to seek out help when I was struggling that added into my rough first year at college. It took me after that first year to really begin excelling in classes. I made a lot of friends and got to explore my interests in the engineering field. Some of the friends I made also helped me in acquiring my first job.

Without the help of Ms. Sheila during this difficult time in my life, I don't think I would have had the opportunity to get to where I am now and to experience a fantastic education at RPI. I wouldn't be able to claim that I have a dual bachelors in Aerospace and Mechanical Engineering.

She was beyond an excellent mentor, and gave me an opportunity I don't think I ever would have had without her help. Ms. Sheila is an excellent character and a trustworthy individual who has made it her mission to help disadvantaged individuals have an opportunity at what otherwise would have been impossible without her assistance and expertise.

Thank you for giving me greater opportunities,
Kyus Spencer`
    },
    {
      name: "Mani-Juliette Sakho",
      school: "Mount Holyoke College, Class of 2030",
      year: "Class of 2030",
      achievement: "Full-Ride Scholarship | First-Generation College Student | Rural West Virginia",
      quote: "What makes Ms. Sheila stand out is not her vast knowledge or immense connections, but the irreplaceable faith she possesses in her students. That kind of belief is hard to come by.",
      fullStory: `I met Ms. Sheila at a Black Policy event at my state's Capitol and had the wonderful opportunity of hearing her speak. Since that moment, I knew this was someone I wanted in my corner. After many brief conversations, I could tell this was a lady that genuinely believed in the students she worked with and was very committed to helping them succeed. It became clear very quickly that this was a woman who could help get me where I needed to be.

As a first-generation student, living in rural West Virginia, my understanding of the college system was very limited. Although I knew I wanted to go out of state, the logistics on how I was going to make that happen felt overwhelming and unclear. I was also unsure of what I wanted to pursue beyond medicine. After many more in-depth conversations, Ms. Sheila helped me chart a very detailed and intentional path for my future. She pushed me to think critically about my goals, ambitions, and the life I wanted for myself.

What makes Ms. Sheila stand out is not her vast knowledge or immense connections, but the irreplaceable faith she possesses in her students. That kind of belief is hard to come by. Additionally, she does not simply tell her students what to do. She guides rather than instructs, allowing her students grow independently while still providing support where it is most needed. While she is extremely critical and honest, she always leads with intention and care.

Since the beginning of our time working together, Ms. Sheila has been consistent and unwavering in her support. Together we have reviewed and analyzed academic programs, edited college essays, and practiced for interviews. Through this process, she taught me what colleges are truly looking for— me! She encourages writing with authenticity and has taught me to lead with it in every aspect of my life. Her expertise is not solely limited to college admissions or applications though. Ms. Sheila has been instrumental in helping navigate academics (statistics was a pain!), plan extracurricular activities, and has been particularly helpful working through family-related challenges.

As someone who is used to working on my academics alone, I believed the college process was a journey that I could tackle by myself. Quickly I learnt the college process only works as a collaboration between the student, the high school, and the parents. It was the last one that I learned was most difficult. My immigrant parents, who hold very traditional beliefs and values, were not ready to let me go hours away for school nor did they understand my decision to. What they saw as disobedience, Ms. Sheila understood as necessary steps towards my growth and independence. While mentoring me, she also guided my parents through their own difficult journey of letting go and understanding my decision was my own to make. It was something that we didn't know that we had to face and I am grateful that I had her support at that time.

Throughout this year, Ms. Sheila has been instrumental in helping me broaden my perspectives, encourage me to pursue opportunities I did not believe I could pursue, and value myself and my abilities.

More than anything, I can confidently say Ms. Sheila installs confidence in the students she mentors and does a fantastic job of placing them where they need to be. If you want someone who will always tell you the truth while actively pushing you to be your best self, Ms Shelia is the mentor for you. I am forever grateful I chose her services! Because of her guidance and support, I will be attending Mount Holyoke College next year on a full-ride scholarship, and I could not be happier! Go Lyons!

Sincerely,
Mani-Juliette Sakho
Mount Holyoke College, Class of 2030`
    }
  ];

  const story = stories[currentIndex];

  return (
    <section id="stories" className="py-24 bg-white" data-testid="stories-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-slideUp">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="Pathways to Prosperity Logo" className="h-24 w-24 object-contain" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Success Stories
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real letters from students and families whose lives have been transformed.
          </p>
        </div>

        <div className="relative">
          <div className="bg-gradient-to-br from-emerald-50 to-amber-50 rounded-3xl p-8 lg:p-12 border border-amber-100">
            <div className="flex items-start gap-4 mb-6">
              <Quote className="w-12 h-12 text-emerald-700 flex-shrink-0" />
              <div>
                <span className="text-emerald-700 font-medium">{story.year}</span>
                <h3 className="font-display text-xl font-bold text-gray-900">{story.school}</h3>
              </div>
            </div>

            <blockquote className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-8">
              "{story.quote}"
            </blockquote>

            <div className="prose prose-lg max-w-none text-gray-600 mb-8 max-h-96 overflow-y-auto">
              <p className="whitespace-pre-line">{story.fullStory}</p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-amber-200">
              <div>
                <h4 className="font-display text-xl font-bold text-gray-900">{story.name}</h4>
                <p className="text-emerald-700 font-medium">{story.achievement}</p>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{currentIndex + 1} of {stories.length}</span>
                <button 
                  className="p-2 rounded-full bg-white border border-amber-200 hover:bg-amber-50 transition-colors disabled:opacity-50"
                  onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                  disabled={currentIndex === 0}
                  data-testid="stories-prev"
                >
                  <ChevronLeft size={20} />
                </button>
                <button 
                  className="p-2 rounded-full bg-white border border-amber-200 hover:bg-amber-50 transition-colors disabled:opacity-50"
                  onClick={() => setCurrentIndex(Math.min(stories.length - 1, currentIndex + 1))}
                  disabled={currentIndex === stories.length - 1}
                  data-testid="stories-next"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 mt-6">
            Authentic letters from our students and families
          </p>
        </div>
      </div>
    </section>
  );
};

// Resources Section
const ResourcesSection = () => {
  const [resources, setResources] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    axios.get(`${API}/resources`).then(r => setResources(r.data)).catch(() => {});
  }, []);

  const categories = ["All", ...new Set(resources.map(r => r.category))];
  const filtered = activeCategory === "All" ? resources : resources.filter(r => r.category === activeCategory);

  return (
    <section id="resources" className="py-24 bg-amber-50/50" data-testid="resources-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center mb-4">
          <img src={LOGO_URL} alt="Pathways to Prosperity Logo" className="h-24 w-24 object-contain" />
        </div>
        <div className="flex items-center justify-center gap-3 mb-6">
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900">
            Free Resources
          </h2>
        </div>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-12">
          Helpful guides and tools to support your educational journey.
        </p>

        {resources.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 border border-amber-100 text-center">
            <BookOpen className="mx-auto mb-6 text-emerald-300" size={64} />
            <h3 className="font-display text-2xl font-bold text-gray-900 mb-3">Coming Soon</h3>
            <p className="text-gray-600 text-lg max-w-md mx-auto">
              We're curating a collection of free resources to help you on your educational journey. Check back soon!
            </p>
          </div>
        ) : (
          <>
            {categories.length > 1 && (
              <div className="flex flex-wrap justify-center gap-2 mb-12">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeCategory === cat 
                        ? 'bg-emerald-700 text-white' 
                        : 'bg-white text-gray-600 hover:bg-emerald-50 border border-amber-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((resource, i) => (
                <div 
                  key={resource.id || i}
                  className="bg-white rounded-2xl p-6 border border-amber-100 shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`resource-${i}`}
                >
                  <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-full mb-4">
                    {resource.category}
                  </span>
                  <h3 className="font-display text-lg font-bold text-gray-900 mb-2">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{resource.description}</p>
                  {resource.link && (
                    <a href={resource.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-emerald-700 font-medium hover:text-emerald-800 transition-colors">
                      <ExternalLink size={18} />
                      View Resource
                    </a>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-12 bg-white rounded-2xl p-8 border border-amber-100 text-center">
          <h3 className="font-display text-xl font-bold text-gray-900 mb-2">Need More Help?</h3>
          <p className="text-gray-600 mb-4">Schedule a consultation and we'll provide personalized resources for your situation.</p>
          <button 
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-secondary"
            data-testid="resources-contact-btn"
          >
            Contact Us
          </button>
        </div>
      </div>
    </section>
  );
};

// Blog Section
const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [openPost, setOpenPost] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");
  const postsPerPage = 6;

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => {
      setPosts(r.data);
      // Auto-open post if URL has ?post=<id>
      const params = new URLSearchParams(window.location.search);
      const postId = params.get("post");
      if (postId) {
        const found = (r.data || []).find(p => p.id === postId);
        if (found) setOpenPost(found);
      }
    }).catch(() => {});
  }, []);

  // Listen for "open-blog-post" events from sibling components (e.g., MostReadBlog)
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.id;
      if (!id) return;
      // posts may not be loaded yet; fetch fresh
      axios.get(`${API}/blog`).then(r => {
        const found = (r.data || []).find(p => p.id === id);
        if (found) {
          setPosts(r.data);
          setOpenPost(found);
        }
      }).catch(() => {});
    };
    window.addEventListener("open-blog-post", handler);
    return () => window.removeEventListener("open-blog-post", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = openPost ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [openPost]);

  // Record a view + update URL when modal opens (privacy-safe, no PII)
  useEffect(() => {
    if (!openPost) return;
    axios.post(`${API}/blog/${openPost.id}/view`).catch(() => {});
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("post", openPost.id);
      window.history.replaceState({}, "", url.toString());
    } catch { /* ignore */ }
  }, [openPost]);

  const closeModal = () => {
    setOpenPost(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("post");
      window.history.replaceState({}, "", url.pathname + url.hash);
    } catch { /* ignore */ }
  };

  const copyPostLink = async (post) => {
    const url = `${window.location.origin}/?post=${post.id}#blog`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus("Link copied!");
    } catch {
      setCopyStatus("Press Ctrl+C to copy");
    }
    setTimeout(() => setCopyStatus(""), 2000);
  };

  const totalPages = Math.ceil(posts.length / postsPerPage);
  const paginatedPosts = posts.slice((page - 1) * postsPerPage, page * postsPerPage);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    const hours = Math.floor(diff / MS_PER_HOUR);
    const days = Math.floor(diff / MS_PER_DAY);
    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <section id="blog" className="py-24 bg-white" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center mb-4">
          <img src={LOGO_URL} alt="Pathways to Prosperity Logo" className="h-24 w-24 object-contain" />
        </div>
        <div className="flex items-center justify-center gap-3 mb-6">
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900">
            Weekly Blog
          </h2>
        </div>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-4">
          Insights, tips, and updates from Auntie Education on Facebook.
        </p>
        <p className="text-center text-gray-500 mb-12">
          Follow us on{' '}
          <a href="https://www.facebook.com/profile.php?id=61575726498498" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">
            Facebook
          </a>
          {' '}for the latest posts!
        </p>

        {posts.length === 0 ? (
          <div className="bg-amber-50 rounded-3xl p-16 border border-amber-100 text-center">
            <FileText className="mx-auto mb-6 text-emerald-300" size={64} />
            <h3 className="font-display text-2xl font-bold text-gray-900 mb-3">Coming Soon</h3>
            <p className="text-gray-600 text-lg max-w-md mx-auto">
              Sheila is preparing insightful articles on scholarships, college admissions, and educational strategies. Stay tuned!
            </p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {paginatedPosts.map((post, i) => (
                <article 
                  key={post.id || i}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-shadow overflow-hidden"
                  data-testid={`blog-post-${i}`}
                >
                  {/* Facebook-style header */}
                  <div className="flex items-center gap-3 p-4 border-b border-gray-100">
                    {post.avatar_id ? (
                      <img
                        src={`${API}/images/${post.avatar_id}`}
                        alt="Auntie Education avatar"
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-200"
                        data-testid={`blog-post-avatar-${i}`}
                      />
                    ) : (
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        f
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm">Auntie Education</p>
                      <p className="text-xs text-gray-500">{formatDate(post.created_at)} &middot; <span className="text-blue-600">{post.category}</span></p>
                    </div>
                  </div>

                  {/* Post content */}
                  <div className="px-4 py-3">
                    <h3 className="font-bold text-gray-900 mb-2">{post.title}</h3>
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                      {post.content.length > 300 ? post.content.substring(0, 300) + '... ' : post.content}
                      {post.content.length > 300 && (
                        <button
                          type="button"
                          onClick={() => setOpenPost(post)}
                          className="text-blue-600 hover:underline font-medium"
                          data-testid={`blog-see-more-${i}`}
                        >
                          See more
                        </button>
                      )}
                    </p>
                  </div>

                  {/* Images */}
                  {post.image_ids && post.image_ids.length > 0 && (
                    <div
                      className={`${post.image_ids.length === 1 ? '' : 'grid grid-cols-2 gap-0.5'} cursor-pointer`}
                      onClick={() => setOpenPost(post)}
                      data-testid={`blog-images-${i}`}
                    >
                      {post.image_ids.slice(0, 4).map((imgId, idx) => (
                        <img 
                          key={imgId}
                          src={`${API}/images/${imgId}`}
                          alt={`${post.title} - image ${idx + 1}`}
                          className={`w-full object-cover ${post.image_ids.length === 1 ? 'max-h-96' : 'h-48'}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Facebook-style engagement bar */}
                  <div className="px-4 py-3 border-t border-gray-100">
                    <div className="flex items-center justify-between text-gray-500 text-xs">
                      <span>{new Date(post.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      <button
                        type="button"
                        onClick={() => setOpenPost(post)}
                        className="text-blue-600 hover:underline font-medium"
                        data-testid={`blog-read-full-${i}`}
                      >
                        Read full post
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-12">
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="blog-prev"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-10 h-10 rounded-lg font-medium ${page === p ? 'bg-emerald-700 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >
                    {p}
                  </button>
                ))}
                <button 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="blog-next"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Full-Post Modal */}
      {openPost && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
          onClick={closeModal}
          data-testid="blog-modal-overlay"
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full my-8 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="blog-modal"
          >
            {/* Modal header */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-100 sticky top-0 bg-white">
              {openPost.avatar_id ? (
                <img
                  src={`${API}/images/${openPost.avatar_id}`}
                  alt="Auntie Education avatar"
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-200"
                />
              ) : (
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  f
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">Auntie Education</p>
                <p className="text-xs text-gray-500">
                  {new Date(openPost.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} &middot;{' '}
                  <span className="text-blue-600">{openPost.category}</span>
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none p-1"
                aria-label="Close"
                data-testid="blog-modal-close"
              >
                &times;
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5">
              <h3 className="font-bold text-gray-900 text-xl mb-3">{openPost.title}</h3>
              <p className="text-gray-800 text-base leading-relaxed whitespace-pre-line">
                {openPost.content}
              </p>
            </div>

            {/* Modal images */}
            {openPost.image_ids && openPost.image_ids.length > 0 && (
              <div className={`${openPost.image_ids.length === 1 ? '' : 'grid grid-cols-2 gap-1'} px-2 pb-2`}>
                {openPost.image_ids.map((imgId, idx) => (
                  <img
                    key={imgId}
                    src={`${API}/images/${imgId}`}
                    alt={`${openPost.title} - image ${idx + 1}`}
                    className="w-full object-cover rounded"
                  />
                ))}
              </div>
            )}

            {/* Engagement & Share bar */}
            <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center gap-2 bg-gray-50">
              {openPost.facebook_url && (
                <>
                  <a
                    href={openPost.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1877f2] text-white text-sm font-medium hover:bg-[#0c5fbf] transition-colors"
                    data-testid="blog-fb-like"
                  >
                    👍 Like on Facebook
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(openPost.facebook_url)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#1877f2] text-[#1877f2] text-sm font-medium hover:bg-blue-50 transition-colors"
                    data-testid="blog-fb-share"
                  >
                    ↗ Share to Facebook
                  </a>
                </>
              )}
              <button
                type="button"
                onClick={() => copyPostLink(openPost)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-white transition-colors"
                data-testid="blog-copy-link"
              >
                🔗 Copy link
              </button>
              {copyStatus && <span className="text-emerald-700 text-xs font-medium ml-1">{copyStatus}</span>}
              <div className="ml-auto">
                <button
                  onClick={closeModal}
                  className="bg-emerald-700 text-white px-4 py-1.5 rounded-md text-sm font-medium hover:bg-emerald-800"
                  data-testid="blog-modal-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    student_name: '',
    parent_guardian_name: '',
    home_address: '',
    city: '',
    state: '',
    zip_code: '',
    high_school_name: '',
    hs_code: '',
    graduation_year: '',
    email: '',
    phone: '',
    additional_info: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const states = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
  ];

  const graduationYears = ['2025', '2026', '2027', '2028', '2029', '2030'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      setSubmitMessage('Thank you! Your inquiry has been submitted. We will contact you within 24-48 hours.');
      setFormData({
        student_name: '',
        parent_guardian_name: '',
        home_address: '',
        city: '',
        state: '',
        zip_code: '',
        high_school_name: '',
        hs_code: '',
        graduation_year: '',
        email: '',
        phone: '',
        additional_info: ''
      });
    } catch (error) {
      setSubmitMessage('An error occurred. Please try again or email us directly.');
    }
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 bg-amber-50/50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column - Consultation Info */}
          <div>
            <div className="flex justify-center mb-6">
              <img src={LOGO_URL} alt="Pathways to Prosperity Logo" className="h-44 w-44 object-contain" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 mb-6 text-center">
              Schedule Your Free Consultation
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Take the first step on your pathway to prosperity. Book a free 40-minute virtual consultation to discuss your educational goals and how we can help you achieve them.
            </p>

            <ul className="space-y-4 mb-8">
              {[
                "40-minute FREE first-time Parent/Family consultation",
                "Monday – Thursday: 11:00 AM – 7:30 PM",
                "Friday: 11:00 AM – 6:00 PM",
                "Saturday & Sunday: 12:00 PM – 3:30 PM",
                "Virtual meetings only"
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-gray-700">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                  {item}
                </li>
              ))}
            </ul>

            <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200 mb-8">
              <h4 className="font-display text-lg font-bold text-gray-900 mb-3">Consultation Pricing</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span><strong>First-time Parent/Family Consultation (40 minutes):</strong> FREE</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span><strong>Subsequent Consultations:</strong> $150.00 per hour, payable by Visa and MasterCard via Venmo or CashApp only. Payment details will be provided by email once you agree to a follow-up meeting.</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-amber-100 mb-8">
              <h3 className="font-display text-xl font-bold text-gray-900 mb-4">Book an Appointment</h3>
              <p className="text-gray-600 mb-4">Select a convenient time for your free consultation</p>
              <a 
                href="https://calendar.app.google/pPvPzrqKNFoQEjdx5" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="scheduling-link"
              >
                <Calendar size={20} />
                Open Scheduling Page
              </a>
            </div>

            <p className="text-gray-600">
              Can't find a time that works? Email us at{' '}
              <a href="mailto:sheila@mypath2prosperity.com" className="text-emerald-700 font-bold italic hover:underline">
                <strong><em>Sheila@MyPath2Prosperity.com</em></strong>
              </a>
              {' '}or call us at{' '}
              <a href="tel:+18447286843" className="text-emerald-700 font-bold hover:underline">
                1-844-7-AUNTIE (1-844-728-6843)
              </a>
            </p>

            {/* Contact Info Cards */}
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              <div className="bg-white rounded-xl p-4 border border-amber-100">
                <Mail className="w-6 h-6 text-emerald-700 mb-2" />
                <h4 className="font-semibold text-gray-900 text-sm">Email Us</h4>
                <a href="mailto:sheila@mypath2prosperity.com" className="text-emerald-700 text-sm hover:underline font-bold italic">
                  <strong><em>Sheila@MyPath2Prosperity.com</em></strong>
                </a>
              </div>
              <div className="bg-white rounded-xl p-4 border border-amber-100">
                <Phone className="w-6 h-6 text-emerald-700 mb-2" />
                <h4 className="font-semibold text-gray-900 text-sm">Call Us</h4>
                <p className="text-gray-600 text-sm">By Appointment Only</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-amber-100">
                <MapPin className="w-6 h-6 text-emerald-700 mb-2" />
                <h4 className="font-semibold text-gray-900 text-sm">Service Area</h4>
                <p className="text-gray-600 text-sm">Virtual Services Nationwide</p>
              </div>
            </div>

            <blockquote className="mt-8 p-6 bg-emerald-50 rounded-xl border-l-4 border-emerald-700">
              <p className="text-gray-700 italic mb-2">
                "Every student deserves a chance to succeed. We're here to make sure financial barriers don't stand in the way of your dreams."
              </p>
              <cite className="text-emerald-700 font-medium">— Sheila Coleman-Castells, M.Ed., Founder</cite>
            </blockquote>
          </div>

          {/* Right Column - Inquiry Form */}
          <div>
            <div className="bg-white rounded-3xl p-8 border border-amber-100 shadow-lg">
              <div className="flex items-center gap-3 mb-2">
                <img src={LOGO_URL} alt="Pathways to Prosperity Logo" className="h-16 w-16 object-contain" />
                <h3 className="font-display text-2xl font-bold text-gray-900">Get in Touch</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Ready to start your journey? Fill out the inquiry form and we'll connect with you within 24-48 hours.
              </p>
              <div className="mb-8 space-y-2">
                <a href="tel:+18447286843" className="inline-flex items-center gap-2 text-emerald-700 font-bold hover:text-emerald-800 transition-colors">
                  <Phone size={18} />
                  1-844-7-AUNTIE (1-844-728-6843)
                </a>
                <br />
                <a href="mailto:sheila@mypath2prosperity.com" className="inline-flex items-center gap-2 text-emerald-700 font-bold italic hover:text-emerald-800 transition-colors">
                  <Mail size={18} />
                  <strong><em>Sheila@MyPath2Prosperity.com</em></strong>
                </a>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Student Information</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student Name *</label>
                      <input
                        type="text"
                        name="student_name"
                        value={formData.student_name}
                        onChange={handleChange}
                        required
                        className="form-input"
                        data-testid="input-student-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Parent/Guardian Name(s) *</label>
                      <input
                        type="text"
                        name="parent_guardian_name"
                        value={formData.parent_guardian_name}
                        onChange={handleChange}
                        required
                        className="form-input"
                        data-testid="input-parent-name"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Home Address *</label>
                  <input
                    type="text"
                    name="home_address"
                    value={formData.home_address}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-address"
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="input-state"
                    >
                      <option value="">Select</option>
                      {states.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP *</label>
                    <input
                      type="text"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="input-zip"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">High School Name *</label>
                    <input
                      type="text"
                      name="high_school_name"
                      value={formData.high_school_name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="input-high-school"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">HS Code (Optional)</label>
                    <input
                      type="text"
                      name="hs_code"
                      value={formData.hs_code}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="input-hs-code"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Graduation Year *</label>
                  <select
                    name="graduation_year"
                    value={formData.graduation_year}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-graduation-year"
                  >
                    <option value="">Select year</option>
                    {graduationYears.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Contact Information</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number (Optional)</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
                  <textarea
                    name="additional_info"
                    value={formData.additional_info}
                    onChange={handleChange}
                    rows={4}
                    className="form-input"
                    placeholder="Tell us about your educational goals, questions, or any specific needs..."
                    data-testid="input-additional-info"
                  />
                </div>

                {submitMessage && (
                  <div className={`p-4 rounded-xl ${submitMessage.includes('error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {submitMessage}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="btn-primary w-full py-4 text-lg disabled:opacity-50"
                  data-testid="submit-inquiry"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Pathways to Prosperity" className="h-20 w-auto" />
            <div>
              <span className="font-display text-xl font-bold block">Pathways to Prosperity, LLC</span>
              <a href="tel:+18447286843" className="text-emerald-400 font-semibold hover:text-emerald-300 transition-colors text-sm">
                1-844-7-AUNTIE (1-844-728-6843)
              </a>
              <a href="mailto:sheila@mypath2prosperity.com" className="text-emerald-400 font-bold italic hover:text-emerald-300 transition-colors text-sm">
                Sheila@MyPath2Prosperity.com
              </a>
            </div>
          </div>
          <div className="flex flex-col items-center gap-3">
            <a
              href="/privacy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-900/40 border border-emerald-500/30 rounded-full hover:bg-emerald-900/60 transition-colors group"
              data-testid="privacy-badge"
              aria-label="Privacy First — read our Privacy Policy"
            >
              <ShieldCheck className="text-emerald-400 group-hover:text-emerald-300" size={18} />
              <span className="text-emerald-300 font-semibold text-sm group-hover:text-emerald-200">Privacy First</span>
            </a>
            <p className="text-gray-400 text-center text-sm">
              © {new Date().getFullYear()} Pathways to Prosperity, LLC.
              {" · "}
              <a href="/privacy" className="hover:text-emerald-400 transition-colors underline" data-testid="footer-privacy-link">Privacy Policy</a>
            </p>
          </div>
          <a 
            href="https://app.emergent.sh/?utm_source=emergent-badge" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <img 
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4" 
              alt="Emergent" 
              className="h-6 w-6 rounded-full"
            />
            Made with Emergent
          </a>
        </div>
      </div>
    </footer>
  );
};

// Main Site Component
const MainSite = () => (
  <div className="App">
    <Navbar />
    <main>
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <SuccessStoriesSection />
      <MostReadBlog />
      <ResourcesSection />
      <BlogSection />
      <ContactSection />
    </main>
    <Footer />
  </div>
);

// Fire a privacy-safe page view ping (called once per route on the main site)
const trackPageView = () => {
  try {
    axios.post(`${API}/track/pageview`, {
      path: window.location.pathname,
      referrer: document.referrer || "",
    }).catch(() => {});
  } catch { /* ignore */ }
};

// Main App Component with Routing
function App() {
  useEffect(() => {
    trackPageView();
  }, []);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/intake" element={<IntakeForm />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="*" element={<MainSiteWithBanner />} />
      </Routes>
    </BrowserRouter>
  );
}

const MainSiteWithBanner = () => (
  <>
    <MainSite />
    <CookieBanner />
  </>
);

export default App;
